/*
 * Forage minigame — 30 seconds, walk over mushrooms to collect them.
 *
 * Mushrooms spawn up to a soft cap (6 visible at a time), grow in over ~0.4s,
 * and are removed when the player is within 22px of them. Score persists to
 * `save.bestForage` on end.
 */

import type { ForageState, PlayerCritter } from '@/state/types';
import { WORLD } from '@/render/palette';
import { save, persist } from '@/state/save';

export const FORAGE_DURATION_SEC = 30;
export const FORAGE_TARGET_COUNT = 6;
export const FORAGE_COLLECT_RADIUS = 22;

export function createForageState(): ForageState {
  return { active: false, mushrooms: [], score: 0, timeLeft: 0 };
}

export function startForage(state: ForageState): void {
  state.active = true;
  state.mushrooms = [];
  state.score = 0;
  state.timeLeft = FORAGE_DURATION_SEC;
}

/** Tick the forage state by dt seconds. Returns true if it ended this tick. */
export function tickForage(
  state: ForageState,
  dt: number,
  player: PlayerCritter
): boolean {
  if (!state.active) return false;
  state.timeLeft -= dt;

  // Maintain target population.
  while (state.mushrooms.length < FORAGE_TARGET_COUNT) {
    state.mushrooms.push({
      x: 70 + Math.random() * (WORLD.width - 140),
      y: 200 + Math.random() * (WORLD.height - 280),
      scale: 0,
      targetScale: 1.2 + Math.random() * 0.5,
    });
  }

  // Grow-in.
  for (const m of state.mushrooms) {
    m.scale = Math.min(m.targetScale, m.scale + dt * 2.4);
  }

  // Collect any mushroom the player is on top of (but only if it's mostly grown).
  state.mushrooms = state.mushrooms.filter((m) => {
    if (m.scale < 0.85) return true;
    const dist = Math.hypot(m.x - player.x, m.y - player.y);
    if (dist < FORAGE_COLLECT_RADIUS) {
      state.score++;
      return false;
    }
    return true;
  });

  if (state.timeLeft <= 0) {
    state.active = false;
    state.mushrooms = [];
    // Record best-ever and persist.
    if (state.score > save.bestForage) save.bestForage = state.score;
    void persist();
    return true;
  }
  return false;
}
